import AppsIcon from '@material-design-icons/svg/filled/apps.svg?react';
import ArchiveIcon from '@material-design-icons/svg/filled/archive.svg?react';
import CheckIcon from '@material-design-icons/svg/filled/check.svg?react';
import CloudDownloadIcon from '@material-design-icons/svg/filled/cloud_download.svg?react';
import EditIcon from '@material-design-icons/svg/filled/edit.svg?react';
import MoreHorizIcon from '@material-design-icons/svg/filled/more_horiz.svg?react';
import SyncIcon from '@material-design-icons/svg/filled/sync.svg?react';
import WarningIcon from '@material-design-icons/svg/filled/warning.svg?react';
import type { DappStatus } from '@src/dto/Dapp';
import type { DappActionResult } from '@src/dto/SuperDappDetail';
import { use, useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { toast } from 'sonner';
import {
  BackLink,
  DialogAction,
  HeaderAction,
  headerIconButtonClassName,
  menuContentClassName,
  menuItemClassName,
  MetaSeparator,
} from '../../components/common/detailChrome';
import { CopyableValue } from '../../components/common/detailParts';
import {
  StatusBadge,
  TypedStatusBadge,
} from '../../components/common/StatusBadge';
import { S3Image } from '../../components/S3Image';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { TRPCContext } from '../../contexts/TRPC';
import { useCategoryAssignmentAccess } from '../../hooks/useCategoryAssignmentAccess';
import { useDappCategories } from '../../hooks/useDappCategories';
import { usePageTitle } from '../../hooks/usePageTitle';
import { useRequireAuth } from '../../hooks/useRequireAuth';
import { downloadFile, formatRelativeTime } from '../../lib/formatters';
import { isLeftResult, isRightResult } from '../../lib/trpcTypeGuards';
import { cn } from '../../lib/utils';
import { captureAndReportError } from '../../utils/errorReporting';
import {
  formatBytes,
  formatDateTimeWithLocalZone,
} from '../../utils/stringHelpers';
import {
  TabNavigation,
  tabId,
  tabPanelId,
} from '../../components/common/TabNavigation';
import {
  buildActivity,
  describeStoreStatus,
  type StoreStatus,
  versionLabel,
} from './dappSignals';
import { superDappTabs } from './tabConfig';
import { mediaCount } from './tabs/MediaTab';
import type { SuperDappDetailData, SuperDappTabKey } from './types';
import { useSuperDappDetail } from './useSuperDappDetail';

/**
 * Detail tabs hidden from non-admin reviewers: their data (publisher PII/KYB,
 * raw DB records, sync state, file metadata) has no corresponding reviewer
 * capability, so it stays admin-only. The API strips the data as well.
 */
const ADMIN_ONLY_TAB_KEYS = new Set<SuperDappTabKey>(['publisher', 'system']);

/**
 * Every timestamp on this page renders through here so an absent date reads as
 * the same em dash the `Empty` value placeholder uses.
 */
function formatDate(timestamp: number | null): string {
  return timestamp ? formatDateTimeWithLocalZone(timestamp) : '—';
}

const DAPP_STATUSES: DappStatus[] = [
  'Draft',
  'UnderReview',
  'Published',
  'Rejected',
  'Archived',
];

interface SuperDappDetailPageProps {
  resolvedDappId?: string;
}

export function SuperDappDetailPage({
  resolvedDappId,
}: SuperDappDetailPageProps = {}) {
  const { id: routeDappId } = useParams<{ id: string }>();
  const id = resolvedDappId ?? routeDappId;
  const access = useCategoryAssignmentAccess();
  const { canRender: isAuthorizedRole } = useRequireAuth({
    requireRole: 'hubspotTickets',
    redirectUnauthorizedTo: '/dashboard',
  });
  const navigate = useNavigate();
  const trpc = use(TRPCContext);
  const [searchParams, setSearchParams] = useSearchParams();

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<DappStatus>('Draft');
  const [statusReason, setStatusReason] = useState('');
  // The Store lookup is not refetched after publishing; remember the outcome.
  const [publishedOverride, setPublishedOverride] = useState(false);

  const handleLoadError = useCallback(
    (message: string) => {
      toast.error(message);
      navigate('/dapps');
    },
    [navigate],
  );

  const { data, reload } = useSuperDappDetail({
    dappId: id,
    trpc,
    onError: handleLoadError,
  });
  const store = useDappCategories(id ?? null, {
    canEdit: access.canAssignCategories,
  });

  usePageTitle(
    data ? `${data.dapp.dappName} | Dapps` : 'Dapp | Publishing Portal',
  );

  useEffect(() => {
    if (data) {
      setSelectedStatus(data.dapp.workflowStatus);
    }
  }, [data]);

  useEffect(() => {
    setPublishedOverride(false);
  }, [id]);

  const isAdmin = access.isAdmin;
  const visibleTabs = isAdmin
    ? superDappTabs
    : superDappTabs.filter((tab) => !ADMIN_ONLY_TAB_KEYS.has(tab.key));

  const requestedTab = searchParams.get('tab');
  const activeTabDefinition =
    visibleTabs.find((tab) => tab.key === requestedTab) ?? visibleTabs[0];
  const ActiveTabComponent = activeTabDefinition.component;

  const setActiveTab = useCallback(
    (tab: SuperDappTabKey) => {
      setSearchParams(
        (previous) => {
          const next = new URLSearchParams(previous);
          if (tab === 'overview') {
            next.delete('tab');
          } else {
            next.set('tab', tab);
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const canRender = isAuthorizedRole && !access.isLoading;

  const performAction = async (
    action: 'updateStatus' | 'deleteData' | 'triggerSync' | 'exportData',
    params: Record<string, unknown>,
  ) => {
    if (!id) return null;

    try {
      const result = await trpc.client.performDappAction.mutate({
        dappId: id,
        action,
        params,
      });

      if (isRightResult(result)) {
        toast.success(result.right.message);
        return result.right as DappActionResult;
      }

      if (isLeftResult(result)) {
        toast.error(result.left.message);
      }
    } catch (error) {
      captureAndReportError(error, {
        operation: `Failed to perform action ${action}`,
        component: 'SuperDappDetailPage',
      });
      toast.error(
        `Failed to ${action.replace(/([A-Z])/g, ' $1').toLowerCase()}`,
      );
    }

    return null;
  };

  const handleStatusUpdate = async () => {
    const result = await performAction('updateStatus', {
      status: selectedStatus,
      reason: statusReason,
    });

    if (result) {
      setShowStatusModal(false);
      setStatusReason('');
      reload();
    }
  };

  const handleDelete = async () => {
    const result = await performAction('deleteData', { permanent: false });
    if (result) {
      navigate('/dapps');
    }
  };

  const handleSync = async () => {
    await performAction('triggerSync', {});
  };

  const handleExport = async () => {
    if (!data) return;
    // The server call records the export in the audit log; the payload it
    // would return is the detail we already hold, so download that.
    const result = await performAction('exportData', { format: 'json' });
    if (result) {
      downloadFile(
        JSON.stringify(data, null, 2),
        `${data.dapp.packageName ?? data.dapp.id}.json`,
        'application/json',
      );
    }
  };

  if (!canRender) {
    return null;
  }

  if (!data) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-neutral-400">Loading dapp details…</div>
      </div>
    );
  }

  const { dapp } = data;
  const storeStatus: StoreStatus = publishedOverride
    ? 'published'
    : store.info
      ? store.info.storeStatus
      : undefined;

  const tabCounts: Partial<Record<SuperDappTabKey, number>> = {
    releases: data.releases.length,
    media: mediaCount(dapp),
    activity: buildActivity(data, {
      includeEvents: access.canViewAuditTrail,
    }).length,
    system: data.files.length,
  };

  return (
    <>
      <div className="flex-1 overflow-y-auto">
        <div className="sticky top-0 z-20 border-b border-white/5 bg-[#161717]/95 backdrop-blur-xl">
          <div className="mx-auto w-full max-w-6xl px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between gap-3 pt-3">
              <BackLink to="/dapps">Dapps</BackLink>
              {isAdmin && (
                <div className="flex items-center gap-1.5">
                  <HeaderAction
                    icon={<EditIcon className="size-3.5 fill-current" />}
                    onClick={() => setShowStatusModal(true)}
                  >
                    Change status
                  </HeaderAction>
                  <MoreMenu
                    onExport={handleExport}
                    onSync={handleSync}
                    onArchive={() => setShowDeleteModal(true)}
                  />
                </div>
              )}
            </div>

            <IdentityHeader
              data={data}
              storeStatus={storeStatus}
              showPublisher={isAdmin}
            />

            <TabNavigation
              tabs={visibleTabs}
              activeTab={activeTabDefinition.key}
              counts={tabCounts}
              idPrefix="dapp"
              ariaLabel="Dapp sections"
              onTabChange={setActiveTab}
            />
          </div>
        </div>

        <div
          id={tabPanelId('dapp', activeTabDefinition.key)}
          role="tabpanel"
          aria-labelledby={tabId('dapp', activeTabDefinition.key)}
          className="mx-auto w-full max-w-6xl px-4 py-5 sm:px-6 lg:px-8"
        >
          <ActiveTabComponent
            data={data}
            store={store}
            storeStatus={storeStatus}
            onStorePublished={() => setPublishedOverride(true)}
            formatDate={formatDate}
            formatBytes={formatBytes}
            reload={reload}
            isAdmin={isAdmin}
            canViewAuditTrail={access.canViewAuditTrail}
            canAssignCategories={access.canAssignCategories}
            canPublish={access.canPublish}
            onTabChange={setActiveTab}
          />
        </div>
      </div>

      <StatusModal
        open={showStatusModal}
        dappName={dapp.dappName}
        currentStatus={dapp.workflowStatus}
        selectedStatus={selectedStatus}
        statusReason={statusReason}
        onStatusChange={setSelectedStatus}
        onReasonChange={setStatusReason}
        onClose={() => {
          setShowStatusModal(false);
          setStatusReason('');
        }}
        onConfirm={handleStatusUpdate}
      />

      <ArchiveModal
        open={showDeleteModal}
        dappName={dapp.dappName}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={() => {
          setShowDeleteModal(false);
          void handleDelete();
        }}
      />
    </>
  );
}

/** Icon, name, the two state chips, and the one-line facts an admin scans first. */
function IdentityHeader({
  data,
  storeStatus,
  showPublisher,
}: {
  data: SuperDappDetailData;
  storeStatus: StoreStatus;
  showPublisher: boolean;
}) {
  const { dapp, publisher, stats } = data;
  const publisherName =
    publisher.organization?.name ?? publisher.individual?.name ?? null;
  const hasPublishedVersion =
    dapp.publishedVersionName !== null || dapp.publishedVersionCode !== null;

  return (
    <div className="flex items-start gap-3.5 pb-4 pt-3">
      <S3Image
        src={dapp.dappIconUrl}
        thumbnailSrc={dapp.dappIconThumbnailUrl}
        alt=""
        aria-hidden="true"
        className="size-12 shrink-0 rounded-xl object-cover ring-1 ring-white/10"
        fallback={
          <div className="flex size-12 shrink-0 items-center justify-center rounded-xl bg-white/[0.04] ring-1 ring-white/10">
            <AppsIcon className="size-5 fill-current text-neutral-500" />
          </div>
        }
      />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="truncate text-xl font-semibold tracking-tight text-white sm:text-2xl">
            {dapp.dappName}
          </h1>
          <TypedStatusBadge type="dapp" status={dapp.status} />
          {storeStatus !== undefined && (
            <StatusBadge {...describeStoreStatus(storeStatus)} />
          )}
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-x-2.5 gap-y-1 text-xs text-neutral-500">
          {dapp.packageName ? (
            <CopyableValue
              value={dapp.packageName}
              className="-my-1.5 max-w-full"
            />
          ) : (
            <span>No package</span>
          )}
          <MetaSeparator />
          <span>
            {hasPublishedVersion
              ? `${versionLabel(dapp.publishedVersionName, dapp.publishedVersionCode)} live`
              : 'Not published'}
          </span>
          {showPublisher && publisherName && (
            <>
              <MetaSeparator />
              <Link
                to={`/publishers/${publisher.id}`}
                className="truncate text-neutral-300 transition-colors hover:text-white"
              >
                {publisherName}
              </Link>
            </>
          )}
          <MetaSeparator />
          <span className="tabular-nums">
            {stats.totalReleases}{' '}
            {stats.totalReleases === 1 ? 'release' : 'releases'}
          </span>
          {dapp.updated && (
            <>
              <MetaSeparator />
              <span title={formatDate(dapp.updated)}>
                Updated {formatRelativeTime(dapp.updated)}
              </span>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/** Rare admin operations, kept out of the way of the page's real work. */
function MoreMenu({
  onExport,
  onSync,
  onArchive,
}: {
  onExport: () => void;
  onSync: () => void;
  onArchive: () => void;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label="More actions"
          className={headerIconButtonClassName}
        >
          <MoreHorizIcon className="size-4 fill-current" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className={menuContentClassName}>
        <DropdownMenuItem onSelect={onExport} className={menuItemClassName}>
          <CloudDownloadIcon />
          Export JSON
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={onSync} className={menuItemClassName}>
          <SyncIcon />
          Trigger sync
        </DropdownMenuItem>
        <DropdownMenuSeparator className="my-1 h-px bg-neutral-800" />
        <DropdownMenuItem
          onSelect={onArchive}
          className={cn(
            menuItemClassName,
            'text-orange-300 focus:text-orange-200',
          )}
        >
          <ArchiveIcon />
          Archive dapp
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function StatusModal({
  open,
  dappName,
  currentStatus,
  selectedStatus,
  statusReason,
  onStatusChange,
  onReasonChange,
  onClose,
  onConfirm,
}: {
  open: boolean;
  dappName: string;
  currentStatus: DappStatus;
  selectedStatus: DappStatus;
  statusReason: string;
  onStatusChange: (status: DappStatus) => void;
  onReasonChange: (reason: string) => void;
  onClose: () => void;
  onConfirm: () => void;
}) {
  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-w-md rounded-2xl border-white/10 bg-[#161717] sm:rounded-2xl">
        <DialogHeader>
          <DialogTitle className="text-base">Change status</DialogTitle>
          <DialogDescription className="text-xs">{dappName}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div
            role="radiogroup"
            aria-label="New status"
            className="grid grid-cols-2 gap-2"
          >
            {DAPP_STATUSES.map((status) => {
              const isSelected = status === selectedStatus;

              return (
                <button
                  type="button"
                  key={status}
                  role="radio"
                  aria-checked={isSelected}
                  onClick={() => onStatusChange(status)}
                  className={cn(
                    'flex items-center justify-between gap-2 rounded-xl border px-3 py-2.5 text-left transition-colors',
                    isSelected
                      ? 'border-teal-400/40 bg-teal-500/[0.08]'
                      : 'border-white/5 bg-white/[0.02] hover:bg-white/[0.05]',
                  )}
                >
                  <TypedStatusBadge type="dapp" status={status} />
                  {status === currentStatus ? (
                    <span className="text-[10px] font-medium uppercase tracking-[0.08em] text-neutral-500">
                      Current
                    </span>
                  ) : (
                    isSelected && (
                      <CheckIcon
                        aria-hidden="true"
                        className="size-3.5 shrink-0 fill-teal-300"
                      />
                    )
                  )}
                </button>
              );
            })}
          </div>

          <div className="space-y-1.5">
            <label
              htmlFor="dapp-status-reason"
              className="text-[10.5px] font-medium uppercase tracking-[0.08em] text-neutral-500"
            >
              Reason (optional)
            </label>
            <textarea
              id="dapp-status-reason"
              aria-label="Reason"
              value={statusReason}
              onChange={(event) => onReasonChange(event.target.value)}
              rows={3}
              placeholder="Why is the status changing? The publisher is notified."
              className="w-full resize-none rounded-xl border border-white/10 bg-black/20 px-3 py-2.5 text-sm text-white/90 transition-colors placeholder:text-neutral-600 hover:border-white/20 focus:border-teal-500/60 focus:outline-none focus:ring-2 focus:ring-teal-500/15"
            />
          </div>
        </div>

        <DialogFooter className="gap-2">
          <DialogAction onClick={onClose}>Cancel</DialogAction>
          <DialogAction
            onClick={onConfirm}
            disabled={selectedStatus === currentStatus}
            tone="primary"
          >
            Update status
          </DialogAction>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ArchiveModal({
  open,
  dappName,
  onClose,
  onConfirm,
}: {
  open: boolean;
  dappName: string;
  onClose: () => void;
  onConfirm: () => void;
}) {
  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-w-md rounded-2xl border-white/10 bg-[#161717] sm:rounded-2xl">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <span className="flex size-9 shrink-0 items-center justify-center rounded-xl border border-orange-500/20 bg-orange-500/[0.08]">
              <WarningIcon
                aria-hidden="true"
                className="size-4 fill-orange-300"
              />
            </span>
            <div className="min-w-0">
              <DialogTitle className="text-base">Archive dapp</DialogTitle>
              <DialogDescription className="text-xs">
                {dappName}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <p className="text-sm leading-relaxed text-neutral-400">
          The dapp will be marked as deleted and disappear from the dapps list.
          It can be restored later from Deleted Dapps.
        </p>

        <DialogFooter className="gap-2">
          <DialogAction onClick={onClose}>Cancel</DialogAction>
          <DialogAction onClick={onConfirm} tone="caution">
            Archive dapp
          </DialogAction>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
